import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Avatar, Typography, Grid, Button, CircularProgress , Alert } from "@mui/material";
import PremiumIcon from "../../assets/svgs/premiumIcon.svg";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserSubscription } from "../../services/razorpayService";
import { getUserById } from "../../services/userProfileService";
import { formatExpiryDateForDisplay } from "../Subscription/SubscriptionExpiredDialog";
import "./mysubscription.css";

function formatDateLine(raw) {
  if (raw == null || raw === "") return null;
  return formatExpiryDateForDisplay(raw);
}


function pickSubscriptionDate(obj, ...keys) {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (v != null && v !== "") return v;
  }
  return null;
}

function formatDateFromFields(obj, ...keys) {
  return formatDateLine(pickSubscriptionDate(obj, ...keys));
}

function formatMoney(amount, currency) {
  if (amount == null || amount === "") return "—";
  const c = (currency || "INR").toUpperCase();
  const sym = c === "INR" ? "₹" : `${c} `;
  return `${sym}${Number(amount).toLocaleString("en-IN")}`;
}

function intervalLabel(interval) {
  if (!interval) return "";
  const i = String(interval).toLowerCase();
  if (i === "monthly" || i === "month") return "/ month";
  if (i === "yearly" || i === "annual" || i === "year") return "/ year";
  return ` / ${interval}`;
}


function daysUntilEndDate(sub) {
  const endAt = pickSubscriptionDate(sub, "end_at", "endAt");
  if (!endAt) return null;
  const endDate = new Date(endAt);
  endDate.setHours(23, 59, 59, 999);
  const now = new Date();
  return (endDate - now) / 86400000;
}

const UPGRADE_WINDOW_DAYS = 7;

const MySubscription = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const paymentResponse = state?.paymentResponse;

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [current, setCurrent] = useState(null);
  const [pending, setPending] = useState(null);
  const [previous, setPrevious] = useState(null);

  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const run = async () => {
      try {
        const data = await getUserSubscription(userId);
        if (data.success) {
          const legacySub = data.user?.subscription;
          setCurrent(data.current ?? legacySub ?? null);
          setPending(data.pending ?? null);
          setPrevious(data.previous ?? null);

          if (data.user) {
            setUser(data.user);
          } else {
            const profile = await getUserById(userId);
            setUser(profile ?? null);
          }
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [userId]);

  const hasActiveOrPending = Boolean(current || pending);

  const daysUntilCurrentEnds = current ? daysUntilEndDate(current) : null;
  const upgradeEnabled =
    Boolean(current) &&
    !pending &&
    daysUntilCurrentEnds != null &&
    !Number.isNaN(daysUntilCurrentEnds) &&
    daysUntilCurrentEnds <= UPGRADE_WINDOW_DAYS;

  const daysLeftBanner = daysUntilCurrentEnds != null && !Number.isNaN(daysUntilCurrentEnds)
    ? Math.ceil(Math.max(0, daysUntilCurrentEnds))
    : null;

  const isSubscriptionActivating = current && String(current.status ?? "").toLowerCase() === "created";

  if (!userId) {
    return (
      <Box className="ms-message">
        <Typography>Please sign in to view your subscription.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className="ms-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box className="ms-message">
        <Typography>Unable to load profile.</Typography>
      </Box>
    );
  }

  const firstName = user.first_name ?? user.firstName ?? "";
  const lastName = user.last_name ?? user.lastName ?? "";
  const avatar = user.avatar ?? user.profileImage;

  return (
    <Box className="ms-page">
      <Grid container spacing={3} justifyContent="center" alignItems="flex-start">
        <Grid size={{ xs: 12, md: 4 }}>
          <Card className="ms-profile-card">
            <Avatar src={avatar} className="ms-avatar-lg">
              {firstName.charAt(0)}
            </Avatar>
            <Typography variant="h3" className="ms-name">
              {firstName}&nbsp;{lastName}
            </Typography>
            <Typography variant="h4">{user.email}</Typography>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Box className="ms-main-panel">
            <Typography variant="h2" className="ms-section-title">
              My Subscription
            </Typography>
            <br/>
            <Box className="ms-stack">
              {pending && (
                <Card elevation={0} className="ms-sub-card">
                  <Box className="ms-sub-header ms-sub-header--pending">
                    <Avatar className="ms-sub-avatar ms-sub-avatar--pending">
                      <ScheduleIcon className="ms-icon-white" />
                    </Avatar>
                    <Typography variant="h3" className="ms-sub-header-title">
                      Pending Upgrade
                    </Typography>
                  </Box>
                  <CardContent className="ms-card-content">
                    <Typography variant="body1" color="text.secondary" className="ms-body-lead">
                      Your upgrade to the {" "}
                      <strong>{pending.plan_name || "your new plan"}</strong> will be active once the current plan expires. Please ensure Autopay is not disabled.
                    </Typography>
                    <Box className="ms-date-stack">
                      {formatDateFromFields(pending, "start_at", "startAt") && (
                        <Typography variant="body2" color="text.secondary">
                          Started on:{" "}
                          <span className="ms-date-value">
                            {formatDateFromFields(pending, "start_at", "startAt")}
                          </span>
                        </Typography>
                      )}
                      {formatDateFromFields(pending, "next_due_at", "nextDueAt") && (
                        <Typography variant="body2" color="text.secondary">
                          Payment due by:{" "}
                          <span className="ms-date-value--warning">
                            {formatDateFromFields(pending, "next_due_at", "nextDueAt")}
                          </span>
                        </Typography>
                      )}
                      {formatDateFromFields(pending, "end_at", "endAt") && (
                        <Typography variant="body2" color="text.secondary">
                          Access ends on:{" "}
                          <span className="ms-date-value">
                            {formatDateFromFields(pending, "end_at", "endAt")}
                          </span>
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {current && (
                <Card elevation={0} className="ms-sub-card">
                  <Box className="ms-sub-header ms-sub-header--current">
                    <Avatar className="ms-sub-avatar ms-sub-avatar--current">
                      <CheckCircleIcon className="ms-icon-white ms-icon-check" />
                    </Avatar>
                    <Typography variant="h3" className="ms-sub-header-title">
                      Current Plan
                    </Typography>
                  </Box>
                  <CardContent className="ms-card-content">
                    {isSubscriptionActivating ? (
                      <>
                        <Typography variant="h2" className="ms-plan-title">
                          {current.plan_name}
                          <Box component="img" src={PremiumIcon} alt="" className="ms-premium-icon" />
                        </Typography>
                        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                          Your subscription is being activated — this usually completes within a few seconds. Please refresh the page if the status doesn’t update. 
                        </Alert>
                      </>
                    ) : (
                      <>
                        <Typography variant="h2" className="ms-plan-title">
                          {current.plan_name}
                          <Box component="img" src={PremiumIcon} alt="" className="ms-premium-icon" />
                        </Typography>
                        <Typography className="ms-price">
                          {formatMoney(
                            current.plan_amount ?? current.planAmount,
                            current.plan_currency ?? current.planCurrency
                          )}
                          {intervalLabel(current.plan_interval ?? current.planInterval)}
                        </Typography>
                        <Box className="ms-date-stack ms-date-stack--spaced">
                          {formatDateFromFields(current, "start_at", "startAt") && (
                            <Typography variant="body2" color="text.secondary">
                              Started on:{" "}
                              <span className="ms-date-value">
                                {formatDateFromFields(current, "start_at", "startAt")}
                              </span>
                            </Typography>
                          )}
                          {formatDateFromFields(current, "end_at", "endAt") && (
                            <Typography variant="body2" color="text.secondary">
                              Renews / expires on:{" "}
                              <span className="ms-date-value">
                                {formatDateFromFields(current, "end_at", "endAt")}
                              </span>
                            </Typography>
                          )}
                          {formatDateFromFields(current, "next_due_at", "nextDueAt") && (
                            <Typography variant="body2" color="text.secondary">
                              Next payment due:{" "}
                              <span className="ms-date-value">
                                {formatDateFromFields(current, "next_due_at", "nextDueAt")}
                              </span>
                            </Typography>
                          )}
                        </Box>
                        {/* <Typography variant="body2" color="text.secondary" className="ms-desc">
                          {current.plan_description?.trim() ||
                            "Access to all premium features."}
                        </Typography> */}
                        {upgradeEnabled ? (
                          <Box className="ms-expiry-banner">
                            <WarningAmberIcon className="ms-expiry-banner-icon" sx={{ color: "warning.dark" }} aria-hidden/>
                            <Box className="ms-expiry-banner-copy">
                              <Typography
                                component="div"
                                className="ms-expiry-banner-title"
                                sx={{ color: "warning.dark" }}
                              >
                                Your plan expires soon. Would you like to upgrade now?
                              </Typography>
                            </Box>
                            <Button color="primary" variant="contained" endIcon={<ArrowForwardIcon />} className="ms-expiry-banner-btn" onClick={() => navigate("/subscribe")} >
                              Upgrade Plan
                            </Button>
                          </Box>
                        ) : (
                          <Box className="ms-upgrade-row">
                            <Button
                              variant="contained"
                              endIcon={<ArrowForwardIcon />}
                              disabled
                              className="ms-upgrade-btn"
                              onClick={() => navigate("/subscribe")}
                            >
                              Upgrade Plan
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {!hasActiveOrPending && (
                <Card elevation={0} className="ms-sub-card ms-sub-card--empty">
                  <CardContent className="ms-empty-inner">
                    <Inventory2OutlinedIcon className="ms-empty-icon" />
                    <Typography variant="h2" className="ms-empty-heading">
                      No Active Subscription
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      You currently have no active subscription. Subscribe now to access all
                      features.
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {previous && (
                <Card elevation={0} className="ms-sub-card">
                  <Box className="ms-sub-header ms-sub-header--previous">
                    <Avatar className="ms-sub-avatar ms-sub-avatar--previous">
                      <ErrorOutlineIcon className="ms-icon-white ms-icon-check" />
                    </Avatar>
                    <Typography variant="h3" className="ms-sub-header-title">
                      Previous Plan
                    </Typography>
                  </Box>
                  <CardContent className="ms-card-content">
                    <Typography variant="h2" className="ms-plan-title--sm">
                      {previous.plan_name ?? previous.planName}
                    </Typography>
                    <Box className="ms-date-stack ms-date-stack--spaced">
                      {formatDateFromFields(previous, "start_at", "startAt") && (
                        <Typography variant="body2" color="text.secondary">
                          Started on:{" "}
                          <span className="ms-date-value">
                            {formatDateFromFields(previous, "start_at", "startAt")}
                          </span>
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        Expired on:{" "}
                        <span className="ms-date-value--error">
                          {formatDateFromFields(previous, "end_at", "endAt") ?? "—"}
                        </span>
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Your last plan has expired.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      {paymentResponse && paymentResponse.razorpay_subscription_id && (
        <Box className="ms-payment-banner">
          <CheckCircleIcon color="success" className="ms-payment-icon" />
          <Typography variant="h2" fontWeight="bold" color="success">
            Payment Successful!
          </Typography>
          <Typography variant="h2" color="text.secondary">
            Your subscription is now active.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MySubscription;
